import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";

const PDContestLeaders = () => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const userId = authState?.user?._id;
    const token = authState?.token;

    const [records, setRecords] = useState([]);
    const [selected, setSelected] = useState(null);

    const hasFetchedRef = useRef(false);

    // ---------------- FILTER STATES ----------------
    const [search, setSearch] = useState("");
    const [userCodeFilter, setUserCodeFilter] = useState("");
    const [contestFilter, setContestFilter] = useState("");
    const [updatedDate, setUpdatedDate] = useState("");
    const [appliedDate, setAppliedDate] = useState("");

    const handleApply = () => {
        if (!updatedDate) return;
        setAppliedDate(updatedDate);
        fetchList(updatedDate);
    };

    const clearFilters = () => {
        setUpdatedDate("");
        setAppliedDate("");
        setRecords([]);
    };

    const fetchList = async (date) => {
        if (!userId || !token || !date) return;

        try {
            const res = await fetchData({
                url: "/pd/contest/leaders",
                method: "post",
                data: {
                    updatedDate: date
                },
                headers: { Authorization: token }
            });

            if (res?.success) {
                setRecords(res.data || []);
                hasFetchedRef.current = true;
            }
        } catch (err) {
            console.error("PDContestLeaders error:", err);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    // ---------------- FILTER LOGIC ----------------
    const filteredRecords = useMemo(() => {
        return (records || []).filter((item) => {
            if (!updatedDate) return true;

            const itemDate = new Date(item.updatedDate)
                .toISOString()
                .split("T")[0];

            return itemDate === updatedDate;
        });
    }, [records, updatedDate]);

    return (
        <div className="space-y-3 pt-3 border-t-2 border-cyan-200 rounded-xl">

            {/* ---------------- FILTER BAR ---------------- */}
            <div className="bg-[#0f172a] border border-gray-600 rounded-xl p-3 space-y-2">
                <div className="text-xs text-cyan-300 font-bold">
                    🏅 Filter Contest Leaders
                </div>
                <input
                    type="date"
                    value={updatedDate}
                    onChange={(e) => setUpdatedDate(e.target.value)}
                    className="w-full bg-gray-900 text-white p-2 rounded text-xs"
                />
                <div className="flex gap-2">
                    <button
                        onClick={handleApply}
                        disabled={!updatedDate}
                        className={`text-[10px] px-3 py-1 rounded font-bold ${
                            updatedDate
                                ? "bg-cyan-500 text-black"
                                : "bg-gray-700 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                        Apply Filter
                    </button>

                    <button
                        onClick={clearFilters}
                        className="text-[10px] text-cyan-300 underline"
                    >
                        Clear Filter
                    </button>
                </div>
            </div>

            {/* ---------------- LIST ---------------- */}
            <div className="bg-[#111827] rounded-xl border border-gray-800">
                <div className="px-3 py-2 text-xs text-cyan-300 font-bold border-b border-gray-700">
                    🏅 Contest Leaders ({filteredRecords.length})
                </div>

                {filteredRecords.map((item) => (
                    <div key={item._id} className="border-t border-gray-800 p-3">

                        {/* HEADER */}
                        <div
                            onClick={() => setSelected(item)}
                            className="cursor-pointer flex justify-between items-center"
                        >
                            <div>
                                <div className="text-xs font-semibold text-white">
                                    {item.contestName || "Contest"}
                                </div>

                                <div className="text-[10px] text-gray-400">
                                    📅 Updated: {new Date(item.updatedDate).toLocaleDateString("en-IN")}
                                </div>
                            </div>

                            <div className="text-[10px] text-yellow-300">
                                👥 {item.leaders?.length || 0} Leaders
                            </div>
                        </div>

                        {/* DETAILS */}
                        {selected?._id === item._id && (
                            <div className="mt-3 bg-[#0f172a] p-4 rounded-lg border border-cyan-500/20 space-y-3">

                                {/* CONTEST */}
                                <div className="text-sm font-bold text-white">
                                    {item.contestName}
                                </div>

                                <div className="text-[10px] text-gray-400">
                                    📅 Updated: {new Date(item.updatedDate).toLocaleDateString("en-IN")}
                                </div>

                                {/* LEADERS LIST */}
                                <div className="space-y-2">
                                    <div className="text-xs text-cyan-300 font-semibold">
                                        🏆 Leaders List
                                    </div>

                                    {item.leaders?.map((leader, index) => (
                                        <div
                                            key={leader._id || index}
                                            className="bg-gray-900 p-2 rounded border border-gray-700"
                                        >
                                            <div className="text-xs text-white">
                                                {leader.userName || "Unknown"}
                                            </div>

                                            <div className="text-[10px] text-gray-400">
                                                🆔 {leader.userCode || "N/A"}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* REMARKS */}
                                {item.remarks && (
                                    <div className="text-[10px] text-gray-400">
                                        📝 {item.remarks}
                                    </div>
                                )}

                                {/* STATUS */}
                                <div className="text-[10px] text-cyan-400">
                                    {item.isActive ? "🟢 Active" : "🔴 Inactive"}
                                </div>

                                {/* CREATED */}
                                <div className="text-[10px] text-gray-500">
                                    📌 Created: {new Date(item.createdAt).toLocaleDateString("en-IN")}
                                </div>

                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PDContestLeaders;